import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TeksTimeBrandMark from '../components/TeksTimeBrandMark';

const MIN_LEN = 9;

const ForceChangePasswordPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword.length < MIN_LEN) {
      setMessage({ type: 'error', text: `Password must be at least ${MIN_LEN} characters` });
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.password.forceChangePassword(
        formData.newPassword,
        formData.confirmPassword,
        formData.currentPassword || undefined,
      );
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.mustChangePassword = false;
        localStorage.setItem('user', JSON.stringify(parsed));
      }
      setMessage({ type: 'success', text: 'Personal password created. Continuing…' });
      setTimeout(() => window.location.assign('/'), 600);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to update password.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <TeksTimeBrandMark />
        <Typography variant="h5" gutterBottom>
          Create Your Personal Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {user?.email ? `Signed in as ${user.email}. ` : ''}
          You must set a personal password before continuing.
        </Typography>
        {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            fullWidth
            label="Current Password (if known)"
            type={show ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="New Password"
            type={show ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShow(!show)} edge="end">
                    {show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="Confirm Password"
            type={show ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          />
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }} disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Lock />}>
            Save Personal Password
          </Button>
          <Button fullWidth sx={{ mt: 1 }} onClick={logout}>Sign Out</Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForceChangePasswordPage;
